/**
 * theme/colors.ts
 * ──────────────────────────────────────────────────────────────────────────
 * SK Upsell is rendered MONOCHROME by deliberate design choice (theme.css,
 * OKLCH chroma-0). Motion families and tones are distinguished by LABEL + DOT
 * and by shade — never by hue. These hex values are the exact sRGB conversions
 * of the OKLCH chroma-0 tokens in DESIGN_INSTRUCTIONS/theme.css.
 *
 * Light theme only (a dark theme is defined in the design but unused).
 */

export const LightColors = {
  // Surfaces
  paper: '#FAFAFA', // oklch(0.985 0 0) — app background
  paper2: '#F5F5F5', // oklch(0.970 0 0) — subtle fills, table header, hover
  paper3: '#EFEFEF', // oklch(0.953 0 0) — tracks, deeper fills
  card: '#FFFFFF', //                    — cards, rows, inputs

  // Ink (text)
  ink: '#121212', // oklch(0.18 0 0) — primary text / primary buttons
  ink2: '#424242', // oklch(0.38 0 0) — secondary text
  ink3: '#717171', // oklch(0.55 0 0) — tertiary / muted
  ink4: '#A4A4A4', // oklch(0.72 0 0) — faint labels, placeholders

  // Lines
  line: '#E1E1E1', // oklch(0.91 0 0)
  lineStrong: '#CACACA', // oklch(0.84 0 0)

  // Accent (near-black in mono)
  accent: '#1B1B1B', // oklch(0.22 0 0)
  accentInk: '#060606', // oklch(0.12 0 0)
  accentSoft: '#EDEDED', // oklch(0.945 0 0)
  accentLine: '#C4C4C4', // oklch(0.82 0 0)

  // Semantic tones — all chroma-0 grays of varying lightness so chips stay
  // distinguishable by shade without color.
  info: '#585858',
  infoSoft: '#F0F0F0',
  infoLine: '#CACACA',

  success: '#292929',
  successSoft: '#E9E9E9',
  successLine: '#BEBEBE',

  warn: '#7A7A7A',
  warnSoft: '#EDEDED',
  warnLine: '#C4C4C4',

  danger: '#3D3D3D',
  dangerSoft: '#E8E8E8',
  dangerLine: '#BEBEBE',

  neutral: '#696969',
  neutralSoft: '#EEEEEE',
  neutralLine: '#CACACA',

  purple: '#4D4D4D',
  purpleSoft: '#EDEDED',
  purpleLine: '#C4C4C4',

  orange: '#636363',
  orangeSoft: '#EDEDED',
  orangeLine: '#C4C4C4',

  // Aliases
  white: '#FFFFFF',
  black: '#000000',
  transparent: 'transparent',
} as const;

export type ColorScheme = typeof LightColors;

/** Tone keys used by Chip / Alert / motion-family mapping. */
export type Tone = 'info' | 'success' | 'warn' | 'danger' | 'neutral' | 'accent' | 'purple' | 'orange';
